import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Menu } from "lucide-react";
import { BRAND, NAV_LINKS } from "@/lib/brand";
import { useNavigate, useLocation } from "react-router-dom";

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const onLanding = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (href) => {
    setOpen(false);
    if (!onLanding) {
      navigate("/" + href);
      return;
    }
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const goHome = (e) => {
    e.preventDefault();
    if (!onLanding) {
      navigate("/");
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 border-b transition-colors duration-300 ${
        scrolled
          ? "bg-[rgba(11,10,12,0.88)] backdrop-blur border-[rgba(244,234,222,0.14)]"
          : "bg-[rgba(11,10,12,0.6)] backdrop-blur-sm border-[rgba(244,234,222,0.08)]"
      }`}
    >
      <div className="container-narrow flex h-16 sm:h-20 items-center justify-between">
        <a
          href="/"
          onClick={goHome}
          className="flex items-center gap-3 focus-ring rounded-md"
          data-testid="header-logo-link"
        >
          <img
            src={BRAND.logoUrl}
            alt={`${BRAND.name} logo`}
            className="h-10 sm:h-12 w-auto object-contain drop-shadow-[0_4px_12px_rgba(233,75,75,0.25)]"
          />
          <div className="hidden md:flex flex-col leading-none ml-1">
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#B9AFA6]">
              WW LLC · Global Commerce
            </span>
          </div>
        </a>

        <nav className="hidden md:flex items-center gap-1" aria-label="Main navigation">
          {NAV_LINKS.map((link) => (
            <button
              key={link.label}
              data-testid={link.testid}
              onClick={() => goTo(link.href)}
              className="px-3 py-2 text-sm font-medium text-[#EFE3D6] hover:text-white hover:bg-[rgba(244,234,222,0.06)] rounded-md focus-ring transition-colors"
            >
              {link.label}
            </button>
          ))}
          <Button
            data-testid="header-primary-cta-button"
            onClick={() => goTo("#contact")}
            className="ml-3 bg-[#6B1F47] hover:bg-[#7A1F4A] text-[#F4EADE] btn-lift focus-ring"
          >
            Request a proposal
          </Button>
        </nav>

        <div className="md:hidden">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="mobile-menu-trigger"
                aria-label="Open menu"
                className="inline-flex items-center justify-center h-11 w-11 rounded-md border border-[rgba(244,234,222,0.14)] text-[#F4EADE] hover:bg-[rgba(244,234,222,0.06)] focus-ring"
              >
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="bg-[#0B0A0C] border-l border-[rgba(244,234,222,0.14)] text-[#F4EADE]"
            >
              <SheetHeader>
                <SheetTitle className="font-display text-[#F4EADE] flex items-center gap-2">
                  <img src={BRAND.logoUrl} alt="" className="h-8 w-auto" />
                </SheetTitle>
              </SheetHeader>
              <div className="mt-8 flex flex-col gap-1">
                {NAV_LINKS.map((link) => (
                  <button
                    key={link.label}
                    data-testid={`mobile-${link.testid}`}
                    onClick={() => goTo(link.href)}
                    className="text-left px-3 py-3 rounded-md text-base text-[#EFE3D6] hover:bg-[rgba(244,234,222,0.06)]"
                  >
                    {link.label}
                  </button>
                ))}
                <Button
                  data-testid="mobile-header-primary-cta-button"
                  onClick={() => goTo("#contact")}
                  className="mt-4 w-full bg-[#6B1F47] hover:bg-[#7A1F4A] text-[#F4EADE] btn-lift"
                >
                  Request a proposal
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
